/**
 * AVTR Header Cinema JS v1.0
 * AvatarDesigns Design System
 *
 * Transparent navbar → solid on scroll over fullscreen hero.
 * Handles:
 *   — cinema mode detection
 *   — CSS variable computation (announcement + header heights)
 *   — scroll-based state machine (transparent ↔ solid)
 *   — video smooth fade-in (prevents black flash)
 *   — mobile dvh fix (iOS Safari accurate viewport height)
 *   — announcement bar slide-out on scroll
 *   — Shopify design-mode live-reload support
 */

(function () {
  "use strict";

  /* ── CONSTANTS ── */
  var CSS_CINEMA_CLASS = "avtr-cinema-page";
  var CSS_SCROLLED_CLASS = "avtr-cinema-scrolled";
  var CSS_VIDEO_READY = "avtr-video-ready";
  var CSS_LOADED = "avtr-loaded";
  var CSS_MENU_ACTIVE = "avtr-menu-active";

  var SEL_HERO_SECTION = ".avtr-hero-section";
  var SEL_ANNOUNCE = ".announcement-bar-section";
  var SEL_HEADER_SECTION = ".section-header";
  var SEL_MAIN = "#MainContent";
  var SEL_VIDEO = ".avtr-hero__media video";
  var SEL_LOADING = ".avtr-hero__loading";

  var VAR_OFFSET = "--avtr-cinema-offset";
  var VAR_ANNOUNCE_H = "--avtr-announce-h";
  var VAR_CINEMA_H = "--avtr-cinema-h";
  var VAR_DVH = "--avtr-dvh";

  /* ── STATE ── */
  var isCinema = false;
  var scrollTicking = false;
  var scrollThreshold = 0;
  var resizeTimer = null;
  var announceAutoHidden = false;

  /* ────────────────────────────────────────────────
     1. CINEMA MODE DETECTION
     Uses the server-side `avtr-cinema-ready` class added by Liquid
     in theme.liquid for index pages — no DOM traversal needed.
  ──────────────────────────────────────────────── */
  function detectCinema() {
    return document.body.classList.contains("avtr-cinema-ready");
  }

  /* ────────────────────────────────────────────────
     2. CSS VARIABLES — compute real heights
  ──────────────────────────────────────────────── */
  function setCinemaVars() {
    var announceEl = document.querySelector(SEL_ANNOUNCE);
    var headerEl = document.querySelector(SEL_HEADER_SECTION);

    var announceH = announceEl ? announceEl.offsetHeight : 0;
    var headerH = headerEl ? headerEl.offsetHeight : 76;
    var totalOffset = announceH + headerH;

    var root = document.documentElement;
    root.style.setProperty(VAR_ANNOUNCE_H, announceH + "px");
    root.style.setProperty(VAR_CINEMA_H, headerH + "px");
    root.style.setProperty(VAR_OFFSET, totalOffset + "px");

    return { announceH: announceH, headerH: headerH, total: totalOffset };
  }

  /* ────────────────────────────────────────────────
     3. SCROLL THRESHOLD
  ──────────────────────────────────────────────── */
  function calcThreshold() {
    /* Solidify as soon as the user scrolls 10px — matches representclo.com behaviour */
    scrollThreshold = 10;
  }

  /* ────────────────────────────────────────────────
     4. SCROLL STATE MACHINE
  ──────────────────────────────────────────────── */
  function updateScrollState() {
    var scrollY = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollY > scrollThreshold) {
      if (!document.body.classList.contains(CSS_SCROLLED_CLASS)) {
        document.body.classList.add(CSS_SCROLLED_CLASS);
      }
    } else {
      if (document.body.classList.contains(CSS_SCROLLED_CLASS)) {
        document.body.classList.remove(CSS_SCROLLED_CLASS);
      }
    }

    scrollTicking = false;
  }

  function onScroll() {
    if (!scrollTicking) {
      requestAnimationFrame(updateScrollState);
      scrollTicking = true;
    }
  }

  /* ────────────────────────────────────────────────
     5. VIDEO SMOOTH FADE-IN
     Prevents the "black flash" before video data loads
  ──────────────────────────────────────────────── */
  function initVideoLoad() {
    var videos = document.querySelectorAll(SEL_VIDEO);
    if (!videos.length) return;

    videos.forEach(function (video) {
      /* If video already has data, show immediately */
      if (video.readyState >= 2) {
        activateVideo(video);
        return;
      }

      /* Progressive events — fire on first one that works */
      var activated = false;
      function tryActivate() {
        if (activated) return;
        activated = true;
        activateVideo(video);
      }

      video.addEventListener("canplay", tryActivate, { once: true });
      video.addEventListener("loadeddata", tryActivate, { once: true });
      video.addEventListener("playing", tryActivate, { once: true });

      /* Force load */
      if (video.paused) {
        var playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(function () {
            /* Autoplay blocked — still show video */
            tryActivate();
          });
        }
      }

      /* Hard fallback — show video after 2.5s regardless */
      setTimeout(tryActivate, 2500);
    });

    /* Hide loading overlay once video is ready */
    var loadingEl = document.querySelector(SEL_LOADING);
    if (loadingEl) {
      var firstVideo = videos[0];
      var hideLoading = function () {
        loadingEl.classList.add(CSS_LOADED);
      };
      firstVideo.addEventListener("canplay", hideLoading, { once: true });
      setTimeout(hideLoading, 3000);
    }
  }

  function activateVideo(video) {
    video.classList.add(CSS_VIDEO_READY);
  }

  /* ────────────────────────────────────────────────
     6. MOBILE DVH FIX
     iOS Safari inaccurate 100vh — use window.innerHeight
  ──────────────────────────────────────────────── */
  function setDvh() {
    var vh = window.innerHeight;
    document.documentElement.style.setProperty(VAR_DVH, vh + "px");
  }

  /* ────────────────────────────────────────────────
     7. ANNOUNCEMENT BAR SLIDE-OUT ON SCROLL
     In cinema mode, hide the announcement bar after
     the user scrolls past it for a cleaner fixed header.
  ──────────────────────────────────────────────── */
  function initAnnounceSlide() {
    if (!isCinema) return;

    var announceEl = document.querySelector(SEL_ANNOUNCE);
    if (!announceEl) return;

    var announceH = announceEl.offsetHeight || 40;
    var lastY = 0;

    window.addEventListener(
      "scroll",
      function () {
        var currentY = window.pageYOffset;

        /* Slide out: when scrolled past 1× announce height going down */
        if (currentY > announceH && currentY > lastY && !announceAutoHidden) {
          announceEl.style.transition =
            "transform 0.4s cubic-bezier(0.16,1,0.3,1)";
          announceEl.style.transform = "translateY(-100%)";
          document.documentElement.style.setProperty(VAR_ANNOUNCE_H, "0px");
          document.documentElement.style.setProperty(
            VAR_OFFSET,
            (parseInt(
              getComputedStyle(document.documentElement).getPropertyValue(
                VAR_CINEMA_H,
              ),
            ) || 76) + "px",
          );
          announceAutoHidden = true;
        }

        /* Slide in: when scrolling up to near the top */
        if (
          currentY < announceH * 0.5 &&
          currentY < lastY &&
          announceAutoHidden
        ) {
          announceEl.style.transform = "translateY(0)";
          announceAutoHidden = false;
          /* Re-compute offset with announcement bar restored */
          setCinemaVars();
        }

        lastY = currentY;
      },
      { passive: true },
    );
  }

  /* ────────────────────────────────────────────────
     8. HERO ENTRANCE ANIMATION (CSS fallback)
     Only if GSAP isn't loaded / hasn't handled it yet
  ──────────────────────────────────────────────── */
  function initHeroEntrance() {
    /* Defer to GSAP if available */
    if (typeof gsap !== "undefined" || typeof window.gsap !== "undefined") {
      return;
    }

    var content = document.querySelector(".avtr-hero__content");
    if (!content) return;

    /* Simple CSS fallback entrance */
    content.style.opacity = "0";
    content.style.transform = "translateY(28px)";

    /* Double rAF ensures styles are applied before transition starts */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        content.style.transition =
          "opacity 1s cubic-bezier(0.16,1,0.3,1) 0.4s, transform 1s cubic-bezier(0.16,1,0.3,1) 0.4s";
        content.style.opacity = "1";
        content.style.transform = "translateY(0)";
      });
    });
  }

  /* ────────────────────────────────────────────────
     9. RESIZE HANDLER
  ──────────────────────────────────────────────── */
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      setDvh();
      /* SIEMPRE recalcular --avtr-announce-h (necesario para el nav en todas las páginas) */
      if (!announceAutoHidden) {
        setCinemaVars();
      }
      if (isCinema) {
        calcThreshold();
        /* Re-check announcement bar state */
        if (!announceAutoHidden) {
          var announceEl = document.querySelector(SEL_ANNOUNCE);
          if (announceEl) {
            document.documentElement.style.setProperty(
              VAR_ANNOUNCE_H,
              announceEl.offsetHeight + "px",
            );
          }
        }
      }
    }, 150);
  }

  /* ────────────────────────────────────────────────
     13. MENU INTERACTION DETECTION
     Adds .avtr-menu-active to body whenever any header
     <details> (mega-menu, dropdown, search, mobile drawer)
     is toggled open, so the CSS can switch the header to
     solid black immediately — without waiting for a scroll event.
  ──────────────────────────────────────────────── */
  function initMenuActiveDetection() {
    var headerEl = document.querySelector(".section-header");
    if (!headerEl) return;

    /* Count how many <details> are currently open */
    var openCount = 0;

    function updateMenuActiveClass() {
      if (openCount > 0) {
        document.body.classList.add(CSS_MENU_ACTIVE);
      } else {
        document.body.classList.remove(CSS_MENU_ACTIVE);
      }
    }

    /* Attach toggle listener to a single <details> element */
    function watchDetail(detail) {
      detail.addEventListener("toggle", function () {
        if (detail.open) {
          openCount++;
        } else {
          openCount = Math.max(0, openCount - 1);
        }
        updateMenuActiveClass();
      });
    }

    /* Watch all existing <details> inside the header */
    var allDetails = headerEl.querySelectorAll("details");
    allDetails.forEach(watchDetail);

    /* MutationObserver — catch details added dynamically (predictive search etc.) */
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === "DETAILS") watchDetail(node);
          var nested =
            node.querySelectorAll && node.querySelectorAll("details");
          if (nested) nested.forEach(watchDetail);
        });
      });
    });
    mo.observe(headerEl, { childList: true, subtree: true });

    /* Clean up class on outside click (same as Dawn behaviour) */
    document.addEventListener(
      "click",
      function (e) {
        if (!headerEl.contains(e.target)) {
          openCount = 0;
          updateMenuActiveClass();
        }
      },
      true,
    );

    /* Clean up on Escape key */
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        openCount = 0;
        updateMenuActiveClass();
      }
    });
  }

  /* ────────────────────────────────────────────────
     10. HEADER HIDE-ON-SCROLL (All Pages)
     Scroll ↓ → hide header (translateY(-100%))
     Scroll ↑ → show header (translateY(0))
     ──────────────────────────────────────────────── */
  var hideLastY = 0;
  var hideThreshold = 10; /* minimum delta to trigger hide/show */
  var headerHidden = false;
  var hideTicking = false;

  function updateHideState() {
    var headerSection = document.querySelector(".section-header");
    if (!headerSection) { hideTicking = false; return; }

    var currentY = window.pageYOffset || document.documentElement.scrollTop;
    var delta = currentY - hideLastY;

    /* Don't hide if menu/drawer is open */
    var menuActive = document.body.classList.contains("avtr-menu-active");
    /* Don't hide if AVTR mega dropdown is open */
    var megaOpen = document.querySelector(".avtr-mega-dropdown.is-open");
    /* Don't hide if AVTR mobile drawer is open */
    var drawerOpen = document.querySelector(".avtr-drawer.is-open");

    if (menuActive || megaOpen || drawerOpen) {
      headerSection.classList.remove("avtr-nav-hidden");
      headerHidden = false;
      hideLastY = currentY;
      hideTicking = false;
      return;
    }

    /* Scroll down past threshold → hide */
    if (delta > hideThreshold && currentY > 80 && !headerHidden) {
      headerSection.classList.add("avtr-nav-hidden");
      headerHidden = true;
    }

    /* Scroll up past threshold → show */
    if (delta < -hideThreshold && headerHidden) {
      headerSection.classList.remove("avtr-nav-hidden");
      headerHidden = false;
    }

    /* At the very top → always show */
    if (currentY <= 10) {
      headerSection.classList.remove("avtr-nav-hidden");
      headerHidden = false;
    }

    hideLastY = currentY;
    hideTicking = false;
  }

  function onHideScroll() {
    if (!hideTicking) {
      requestAnimationFrame(updateHideState);
      hideTicking = true;
    }
  }

  function initHideOnScroll() {
    hideLastY = window.pageYOffset || document.documentElement.scrollTop;
    window.addEventListener("scroll", onHideScroll, { passive: true });
  }

  /* ────────────────────────────────────────────────
     14. INIT
  ──────────────────────────────────────────────── */
  function init() {
    setDvh();

    isCinema = detectCinema();

    /* SIEMPRE calcular la altura del announcement bar (necesario para posicionar el nav) */
    setCinemaVars();

    if (isCinema) {
      /* avtr-cinema-ready is already on body from Liquid — no class needed */

      calcThreshold();

      /* Initial scroll check (in case page is reloaded mid-scroll) */
      updateScrollState();

      window.addEventListener("scroll", onScroll, { passive: true });

      /* Init video loading smoothly */
      initVideoLoad();

      /* Announcement bar behavior */
      initAnnounceSlide();

      /* Hero entrance animation (GSAP fallback) */
      initHeroEntrance();

      /* Solid-black header on any menu/search interaction */
      initMenuActiveDetection();
    } else {
      /* Not a cinema page — remove scroll/menu classes if present */
      document.body.classList.remove(CSS_SCROLLED_CLASS);
      document.body.classList.remove(CSS_MENU_ACTIVE);
    }

    /* Hide-on-scroll — runs on ALL pages (cinema + collection + etc.) */
    initHideOnScroll();

    window.addEventListener("resize", onResize, { passive: true });
  }

  /* ────────────────────────────────────────────────
     11. SHOPIFY DESIGN MODE
     Re-run when sections are added/removed in the editor
  ──────────────────────────────────────────────── */
  document.addEventListener("shopify:section:load", function () {
    /* Reset announce state */
    announceAutoHidden = false;

    var wascinemainema = isCinema;
    isCinema = detectCinema();

    if (isCinema) {
      setCinemaVars();
      calcThreshold();
      initVideoLoad();
      initMenuActiveDetection();
      if (!wascinemainema) {
        window.addEventListener("scroll", onScroll, { passive: true });
      }
    } else {
      document.body.classList.remove(CSS_SCROLLED_CLASS);
      document.body.classList.remove(CSS_MENU_ACTIVE);
    }
  });

  document.addEventListener("shopify:section:unload", function () {
    isCinema = detectCinema();
    if (!isCinema) {
      document.body.classList.remove(CSS_SCROLLED_CLASS);
    }
  });

  /* ────────────────────────────────────────────────
     12. BOOTSTRAP
  ──────────────────────────────────────────────── */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
